import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * רשימה שמסדרים בגרירה.
 *
 * מחליפה את זוג החצים שהיה בכל שורה. החצים דרשו לחיצה לכל מקום
 * שהפריט זז, ופריט שעבר מהסוף להתחלה היה שמונה לחיצות.
 *
 * הידית אינה רק לעכבר: היא כפתור שאפשר להגיע אליו ב Tab, ואז רווח
 * מרים את הפריט וחצים מזיזים אותו. בלי זה היינו מחליפים ממשק שעובד
 * במקלדת בממשק שלא, וזו לא החלפה אלא נסיגה.
 *
 * הסדר נשמר פעם אחת בסוף הגרירה, עם רשימת המזהים המלאה.
 */
export function SortableList<T extends { id: string }>({
  items,
  onReorder,
  renderItem,
  disabled = false,
  className,
}: {
  items: T[];
  /** מקבלת את המזהים בסדר החדש. נקראת רק כשהסדר באמת השתנה. */
  onReorder: (ids: string[]) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  const sensors = useSensors(
    // סף של 6 פיקסלים: בלעדיו כל לחיצה על כפתור בתוך השורה נחשבת
    // תחילת גרירה, והמחיקה והעריכה מפסיקות להגיב
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = items.findIndex((item) => item.id === active.id);
    const to = items.findIndex((item) => item.id === over.id);
    if (from < 0 || to < 0) return;

    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next.map((item) => item.id));
  }

  if (disabled) {
    return (
      <div className={className}>
        {items.map((item, index) => (
          <Row key={item.id} id={item.id} disabled>
            {renderItem(item, index)}
          </Row>
        ))}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      accessibility={{
        announcements: {
          onDragStart: ({ active }) => `הורם פריט ${position(items, active.id)}.`,
          onDragOver: ({ active, over }) =>
            over
              ? `פריט ${position(items, active.id)} מעל מקום ${position(items, over.id)}.`
              : undefined,
          onDragEnd: ({ active, over }) =>
            over
              ? `פריט ${position(items, active.id)} הונח במקום ${position(items, over.id)}.`
              : 'הפריט הוחזר למקומו.',
          onDragCancel: () => 'הגרירה בוטלה. הפריט חזר למקומו.',
        },
        screenReaderInstructions: {
          draggable:
            'רווח או אנטר מרימים את הפריט. חצים למעלה ולמטה מזיזים אותו, רווח מניח אותו, ו Escape מבטל.',
        },
      }}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className={className}>
          {items.map((item, index) => (
            <Row key={item.id} id={item.id}>
              {renderItem(item, index)}
            </Row>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

const position = (items: { id: string }[], id: string | number) =>
  items.findIndex((item) => item.id === id) + 1;

function Row({
  id,
  disabled = false,
  children,
}: {
  id: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 rounded-sm bg-raised',
        isDragging && 'relative z-10 shadow-card',
      )}
    >
      {!disabled && (
        <button
          type="button"
          aria-label="שינוי סדר בגרירה"
          {...attributes}
          {...listeners}
          className="inline-flex min-h-11 w-8 shrink-0 cursor-grab touch-none items-center justify-center bg-transparent text-muted"
        >
          <GripVertical size={18} strokeWidth={1.75} aria-hidden="true" />
        </button>
      )}
      <div className="min-w-0 grow">{children}</div>
    </div>
  );
}
